import React, { useCallback, useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { Loader2, Pencil } from 'lucide-react';
import { cn } from '../utils/cn';
export const InlineEdit = React.forwardRef(function InlineEdit(
  {
    value,
    url,
    paramName,
    method = 'patch',
    placeholder = 'Click to edit',
    inputType = 'text',
    className,
    onSuccess,
  },
  ref,
) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => {
    setEditValue(value);
  }, [value]);
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);
  const handleSubmit = useCallback(() => {
    if (editValue === value) {
      setEditing(false);
      return;
    }
    setSubmitting(true);
    router[method](
      url,
      {
        [paramName]: editValue,
      },
      {
        preserveScroll: true,
        onSuccess: () => {
          setEditing(false);
          setSubmitting(false);
          onSuccess?.();
        },
        onError: () => {
          setSubmitting(false);
        },
      },
    );
  }, [editValue, value, url, paramName, method, onSuccess]);
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Escape') {
        setEditValue(value);
        setEditing(false);
      }
    },
    [handleSubmit, value],
  );
  if (editing) {
    return (
      <div ref={ref} className={cn('inline-flex items-center gap-2', className)}>
        <input
          ref={inputRef}
          type={inputType}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSubmit}
          onKeyDown={handleKeyDown}
          disabled={submitting}
          className={cn(
            'rounded-md border border-primary-300 bg-surface px-2 py-1 text-sm',
            'text-fg focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/25',
            submitting && 'opacity-50',
          )}
        />
        {submitting && (
          <Loader2 size={16} className="animate-spin text-primary-500" aria-hidden="true" />
        )}
      </div>
    );
  }
  return (
    <div ref={ref} className={cn('group inline-flex items-center gap-1', className)}>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={cn(
          'rounded px-1 py-0.5 text-sm text-fg transition-colors',
          'hover:bg-surface-muted',
          !value && 'text-fg-tertiary italic',
        )}
      >
        {value || placeholder}
      </button>
      <Pencil
        size={14}
        className="text-fg-tertiary opacity-0 transition-opacity group-hover:opacity-100"
        aria-hidden="true"
      />
    </div>
  );
});
